// src/components/Calendar/utils.ts

export const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
] as const;

export const MONTH_NAMES_SHORT = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
] as const;

export const DOW_SHORT = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"] as const;

export interface DayCell {
  day: number;
  muted: boolean;
  iso: string;
}

export function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function toIso(year: number, month: number, day: number): string {
  return `${String(year).padStart(4, "0")}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

export function buildMonthCells(year: number, month: number): DayCell[] {
  const firstOfMonth = new Date(year, month, 1);
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const daysInPrevMonth = new Date(year, month, 0).getDate();
  const jsDow = firstOfMonth.getDay();
  const leadDays = jsDow === 0 ? 6 : jsDow - 1;

  const cells: DayCell[] = [];

  for (let i = leadDays - 1; i >= 0; i--) {
    const day = daysInPrevMonth - i;
    const prevMonth = month === 0 ? 11 : month - 1;
    const prevYear = month === 0 ? year - 1 : year;
    cells.push({ day, muted: true, iso: toIso(prevYear, prevMonth, day) });
  }

  for (let d = 1; d <= daysInMonth; d++) {
    cells.push({ day: d, muted: false, iso: toIso(year, month, d) });
  }

  const remainder = cells.length % 7;
  if (remainder !== 0) {
    const trailing = 7 - remainder;
    for (let d = 1; d <= trailing; d++) {
      const nextMonth = month === 11 ? 0 : month + 1;
      const nextYear = month === 11 ? year + 1 : year;
      cells.push({ day: d, muted: true, iso: toIso(nextYear, nextMonth, d) });
    }
  }

  return cells;
}

export function parseUserDate(input: string): string | null {
  const trimmed = input.trim();
  const parts = trimmed.split("/");
  if (parts.length !== 3) return null;
  const [dd, mm, yyyy] = parts;
  if (!dd || !mm || !yyyy) return null;
  const d = Number(dd); const m = Number(mm); const y = Number(yyyy);
  if (!Number.isInteger(d) || !Number.isInteger(m) || !Number.isInteger(y)) return null;
  if (d < 1 || d > 31 || m < 1 || m > 12 || y < 1900 || y > 2100) return null;
  const iso = toIso(y, m - 1, d);
  const parsed = new Date(iso);
  if (isNaN(parsed.getTime())) return null;
  return iso;
}

export function formatIsoToUserDate(iso: string): string {
  const [yyyy, mm, dd] = iso.split("-");
  if (!yyyy || !mm || !dd) return "";
  return `${dd}/${mm}/${yyyy}`;
}

export function parseIsoToYMD(iso: string | undefined): { year: number; month: number } {
  if (iso && iso.includes("-")) {
    const [y, m] = iso.split("-");
    const year = Number(y);
    const month = Number(m) - 1;
    if (Number.isInteger(year) && Number.isInteger(month) && month >= 0 && month <= 11) {
      return { year, month };
    }
  }
  const t = new Date();
  return { year: t.getFullYear(), month: t.getMonth() };
}

export function yearsAroundRange(start = 2020, end = 2035): number[] {
  const years: number[] = [];
  for (let y = start; y <= end; y++) years.push(y);
  return years;
}